import React from 'react'
import { MapPin, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { projects } from '../data/projects'
import Testimonials from './Testimonials'

import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { usePopup } from '../context/PopupContext'

import SEO from './SEO'

const Projects: React.FC = () => {
  const { ref, isVisible } = useScrollAnimation()
  const { openPopup } = usePopup()

  // Separate regular projects from land parcels
  const regularProjects = projects.filter(p => p.type !== 'Land Parcel');
  const landParcels = projects.filter(p => p.type === 'Land Parcel');

  // Group regular projects by city
  const projectsByCity = regularProjects.reduce((acc, project) => {
    const city = project.city || 'Gurgaon';
    if (!acc[city]) {
      acc[city] = [];
    }
    acc[city].push(project);
    return acc;
  }, {} as Record<string, typeof projects>);

  // Group land parcels by city
  const landParcelsByCity = landParcels.reduce((acc, project) => {
    const city = project.city || 'Dehradun';
    if (!acc[city]) {
      acc[city] = [];
    }
    acc[city].push(project);
    return acc;
  }, {} as Record<string, typeof projects>);

  // Object.entries doesn't guarantee order, so let's define the order we want
  const cityOrder = ['Gurgaon', 'Dehradun', 'Haridwar'];
  const landParcelCityOrder = ['Dehradun', 'Haridwar', 'Mussoorie'];

  return (
    <section id="projects" className="py-20 bg-gray-50" ref={ref}>
      <SEO
        title="Luxury Real Estate Projects in Gurgaon, Dehradun, Haridwar & Mussoorie"
        description="Browse exclusive property listings, land parcels, and luxury apartments in Gurgaon, Dehradun, Haridwar, and Mussoorie. Verified projects with high ROI."
        keywords="Projects in Gurgaon, Land in Dehradun, Plots in Haridwar, Mussoorie Properties, Singh's Best Buy Projects"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Exclusive Land Parcels Section - FIRST */}
        {landParcels.length > 0 && (
          <div className="mb-32">
            <div className={`text-center mb-16 max-w-3xl mx-auto reveal ${isVisible ? 'active' : ''}`}>
              <h2 className="text-primary-500 font-bold tracking-widest uppercase mb-3 text-sm">
                Exclusive Listings
              </h2>
              <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-bricolage">
                Premium <span className="text-primary-600">Land Parcels</span>
              </h3>
              <p className="text-gray-600 text-lg">
                Verified clear titles with no legal encumbrances. Prime locations with high appreciation potential.
              </p>
            </div>

            {landParcelCityOrder.map((city) => {
              const cityLandParcels = landParcelsByCity[city];
              if (!cityLandParcels || cityLandParcels.length === 0) return null;

              return (
                <div key={`land-${city}`} className="mb-20 last:mb-0">
                  <div className={`mb-10 pl-4 border-l-4 border-primary-500 reveal ${isVisible ? 'active' : ''}`}>
                    <h4 className="text-3xl font-bold text-gray-900 font-bricolage">{city}</h4>
                  </div>

                  {/* Land Parcels Slider */}
                  <div className="relative group">
                    <button
                      onClick={() => {
                        const slider = document.getElementById(`slider-${city}`);
                        if (slider) slider.scrollBy({ left: -300, behavior: 'smooth' });
                      }}
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
                      aria-label="Previous slide"
                    >
                      <ArrowRight className="w-6 h-6 rotate-180" />
                    </button>

                    <button
                      onClick={() => {
                        const slider = document.getElementById(`slider-${city}`);
                        if (slider) slider.scrollBy({ left: 300, behavior: 'smooth' });
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
                      aria-label="Next slide"
                    >
                      <ArrowRight className="w-6 h-6" />
                    </button>

                    <div
                      id={`slider-${city}`}
                      className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide"
                      style={{ scrollBehavior: 'smooth' }}
                    >
                      {cityLandParcels.map((parcel, index) => (
                        <div
                          key={parcel.id}
                          className={`min-w-[85vw] md:min-w-[calc(50%-12px)] lg:min-w-[calc(33.33%-16px)] snap-center group relative overflow-hidden rounded-2xl shadow-xl bg-gray-900 transition-all hover:shadow-2xl reveal ${isVisible ? 'active' : ''}`}
                          style={{ transitionDelay: `${index * 100}ms` }}
                        >
                          {/* Image Container */}
                          <div className="relative h-64 md:h-80 w-full overflow-hidden">
                            <img
                              src={parcel.image}
                              alt={parcel.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-80 group-hover:opacity-100"
                              loading="lazy"
                            />

                            {/* Overlay Gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                            {/* Status Badge */}
                            <div className="absolute top-4 left-4">
                              <span className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-white bg-yellow-600">
                                {parcel.status}
                              </span>
                            </div>

                            {/* Content Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                              <h3 className="text-xl font-bold text-white mb-2 font-inter">
                                {parcel.title}
                              </h3>

                              <div className="flex items-center text-gray-300 mb-3 text-sm">
                                <MapPin className="w-4 h-4 mr-1 text-primary-400" />
                                {parcel.location}
                              </div>

                              <div className="border-t border-white/20 pt-3 mt-3">
                                <div className="flex justify-between items-center mb-3">
                                  <div>
                                    <p className="text-xs text-gray-400 uppercase tracking-wider">Area</p>
                                    <p className="text-white font-bold">{parcel.specs?.area}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-400 uppercase tracking-wider">Rate</p>
                                    <p className="text-white font-bold text-sm">{parcel.price}</p>
                                  </div>
                                </div>

                                <button
                                  onClick={() => openPopup(parcel.title)}
                                  className="w-full py-2.5 bg-white text-gray-900 font-bold rounded-lg hover:bg-primary-500 hover:text-white transition-colors text-sm"
                                >
                                  Enquire Now
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Testimonials Section */}
        <div className="mb-0 reveal active">
          <Testimonials />
        </div>

        {/* Regular Projects Section Header */}
        <div className={`text-center mb-16 max-w-3xl mx-auto reveal ${isVisible ? 'active' : ''}`}>
          <h2 className="text-primary-500 font-bold tracking-widest uppercase mb-3 text-sm">
            Featured Listings
          </h2>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-bricolage">
            Exclusive Luxury <span className="text-primary-600">Opportunities</span>
          </h3>
          <p className="text-gray-600 text-lg">
            Handpicked projects that offer superior appreciation, prime locations, and unmatched lifestyle amenities within Gurgaon, Dehradun, and Haridwar.
          </p>
        </div>

        {/* Regular Projects by City */}
        {cityOrder.map((city) => {
          const cityProjects = projectsByCity[city];
          if (!cityProjects || cityProjects.length === 0) return null;

          return (
            <div key={city} id={city.toLowerCase()} className="mb-20 last:mb-0">
              <div className={`mb-10 pl-4 border-l-4 border-primary-500 reveal ${isVisible ? 'active' : ''}`}>
                <h4 className="text-3xl font-bold text-gray-900 font-bricolage">{city} Projects</h4>
              </div>

              {/* Projects Grid for City */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                {cityProjects.map((project, index) => (
                  <div
                    key={project.id}
                    className={`group relative overflow-hidden rounded-2xl shadow-xl bg-white transition-all hover:shadow-2xl reveal stagger-delay-${(index % 4) + 1} ${isVisible ? 'active' : ''}`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >

                    {/* Image Container */}
                    <div className="relative h-80 md:h-96 w-full overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />

                      {/* Overlay Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90" />

                      {/* Status Badge */}
                      <div className="absolute top-4 left-4">
                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-white ${project.status === 'Ready to Move' ? 'bg-green-600' : 'bg-primary-500'
                          }`}>
                          {project.status}
                        </span>
                      </div>

                      {/* Content Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <div className="transform transition-transform duration-300 translate-y-2 group-hover:translate-y-0">
                          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 font-inter">
                            {project.title}
                          </h3>

                          <div className="flex items-center text-gray-300 mb-4 text-sm">
                            <MapPin className="w-4 h-4 mr-1 text-primary-400" />
                            {project.location}
                          </div>

                          <div className="flex items-center justify-between border-t border-white/20 pt-4 mt-4">
                            <div>
                              <p className="text-xs text-gray-400 uppercase tracking-wider">Price</p>
                              <p className="text-white font-bold text-lg">{project.price}</p>
                            </div>

                            <Link
                              to={`/property/${project.slug}`}
                              className="w-12 h-12 rounded-full bg-white/20 hover:bg-primary-500 backdrop-blur-md flex items-center justify-center text-white transition-colors"
                            >
                              <ArrowRight className="w-5 h-5 -rotate-45 group-hover:rotate-0 transition-transform duration-300" />
                            </Link>
                          </div>

                          <button
                            onClick={() => openPopup(project.title)}
                            className="w-full mt-4 py-3 bg-white text-gray-900 font-bold rounded-xl hover:bg-primary-500 hover:text-white transition-colors"
                          >
                            Enquire Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="text-center mt-16">
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-bold hover:bg-primary-600 transition-colors">
            View All Properties
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  )
}

export default Projects
